/**
 * La **ligne d'adresse** recomposée — `Address.street_number` + `Address.street`
 * ([M32](../../../../docs/MODELE-DONNEES.md#m32)).
 *
 * Le prototype sépare les deux champs à la saisie (`num` et `rue`) et les recompose partout où
 * il montre une adresse : « 27 Rue de la Cadaine ». Écrit une seule fois ici — le tableau des
 * demandes, la tournée du terrain et l'en-tête du rapport rendent la même ligne, et trois
 * recompositions divergeraient au premier « 8 bis ».
 *
 * ⚠️ **Le numéro peut manquer sans que rien ne soit cassé**, et c'est le cas de toute adresse
 * déjà en base : le numéro y est dans `street` (« 12 rue des Ormes »), rien ne l'en extrait, et
 * [M1](../../../../docs/MODELE-DONNEES.md#m1) interdit de parser pour le deviner.
 */
export function formatStreetLine(address) {
    const parts = [address.streetNumber, address.street]
        .map((part) => part?.trim() ?? '')
        .filter((part) => part !== '');
    return parts.length === 0 ? null : parts.join(' ');
}
/**
 * La commune qu'une ligne imprime, comme le rapport PDF : le **bourg** d'une commune nouvelle
 * (« Montjean-sur-Loire », pas « Mauges-sur-Loire »). Les filtres et les regroupements restent sur
 * la commune du référentiel.
 */
export function townOf(address) {
    return address.delegatedName ?? address.city;
}
/**
 * Un numéro de voie en tête de ligne : des chiffres, une plage éventuelle (« 12-14 »), un indice
 * collé ou séparé (« 1BIS », « 12 TER », « 8 A »), puis un séparateur et la rue.
 * ⚠️ **Le séparateur est obligatoire** : c'est lui qui fait d'un indice un mot entier — « 3 avenue »
 * n'est pas « 3 a » + « venue ».
 */
const LEADING_STREET_NUMBER = /^(\d+(?:\s*-\s*\d+)?(?:\s*(?:bis|ter|quater|quinquies)|\s*[a-z])?)[\s,]+(\S.*)$/iu;
/**
 * Le numéro de voie **en tête** d'une adresse écrite d'un bloc — « 1BIS Rue Jules Verne » donne
 * `1BIS` et « Rue Jules Verne ». Rien en tête : `streetNumber` vaut `null` et la rue reste entière.
 *
 * C'est l'inverse de `formatStreetLine`, et la seule règle de découpe : l'import d'une liste dont la
 * colonne « N° » manque ou est vide, et l'adresse du lieu lue sur un PDF (#590), passent tous deux
 * par elle.
 *
 * ⚠️ **Une lecture à l'entrée, pas une reprise** (M32) : le numéro reste **tel qu'écrit**, et les
 * adresses déjà en base ne se redécoupent pas (M1) — ni à l'affichage, ni à la modification.
 */
export function splitLeadingStreetNumber(line) {
    const match = LEADING_STREET_NUMBER.exec(line.trim());
    const [, streetNumber, street] = match ?? [];
    if (streetNumber === undefined || street === undefined)
        return { streetNumber: null, street: line };
    return { streetNumber, street: street.trim() };
}
//# sourceMappingURL=street-line.js.map